import { Primary, SelectField } from '@cinch-labs/design-system'
import { dashify, formatNumber } from '@cinch-labs/shared-util'
import { SearchResponse } from '@cinch-nx/data-search'
import React, { useEffect, useState } from 'react'

import { Bucket, Makes, VehicleCondition } from '../../../types/vehicles-data'
import { Price } from '../filters/price/price'
import styles from './search-cars-form.module.css'

export interface SearchCarsFormProps {
  totalStock: number
  makes: Makes[]
  priceBucket: Bucket
  monthlyPriceBucket: Bucket
  vehicleCondition: VehicleCondition
  section?: string
  searchResponse?: SearchResponse
}

interface FieldOption {
  value: string
  label: string
}

export const SearchCarsForm: React.FC<SearchCarsFormProps> = ({
  totalStock,
  makes,
  priceBucket,
  monthlyPriceBucket,
  vehicleCondition,
  section,
  searchResponse,
}) => {
  const [makeOptions, setMakeOptions] = useState<FieldOption[]>(
    makes.map((make) => ({
      value: make.name,
      label: `${make.name} (${make.stock})`,
    })),
  )
  const [selectedMakeOption, setSelectedMakeOption] = useState<string>()

  const [modelOptions, setModelOptions] = useState<FieldOption[]>([])
  const [selectedModelOption, setSelectedModelOption] = useState<string>()

  const [priceFrequencyOptions] = useState<FieldOption[]>([
    {
      value: 'monthly-price',
      label: 'Monthly price',
    },
    {
      value: 'total-price',
      label: 'Total price',
    },
  ])
  const [selectedPriceFrequencyOption, setSelectedPriceFrequencyOption] =
    useState<string>('monthly-price')

  const [selectedMaxTotalPrice, setSelectedMaxTotalPrice] = useState<number>(0)

  const [filteredMakes, setFilteredMakes] = useState<Makes[]>(makes)

  const [searchResultsCount, setSearchResultsCount] =
    useState<number>(totalStock)

  useEffect(() => {
    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )
    if (!selectedMake) return

    if (selectedMake.models?.length) {
      setModelOptions(
        selectedMake.models.map((model) => ({
          value: model.name,
          label: `${model.name} (${model.stock})`,
        })),
      )
    }

    setSearchResultsCount(selectedMake.stock)

    // reset the selected make option label to not display stock
    setMakeOptions(
      makeOptions.map((makeOption) => {
        return makeOption.value === selectedMakeOption
          ? { ...makeOption, label: makeOption.value }
          : makeOption
      }),
    )
  }, [selectedMakeOption])

  useEffect(() => {
    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )
    if (!selectedMake) return

    const selectedModel = selectedMake.models.find(
      (x) => x.name === selectedModelOption,
    )
    if (!selectedModel) return

    setSearchResultsCount(selectedModel.stock)

    // reset the selected model option label to not display stock
    setModelOptions(
      modelOptions.map((modelOption) => {
        return modelOption.value === selectedModelOption
          ? { ...modelOption, label: modelOption.value }
          : modelOption
      }),
    )
  }, [selectedModelOption])

  useEffect(() => {
    setMakeOptions(
      filteredMakes.map((make) => ({
        value: make.name,
        label:
          selectedMakeOption === make.name
            ? make.name
            : `${make.name} (${make.stock})`,
      })),
    )

    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )

    if (!selectedMake) {
      //set to filtered results total count
      //setSearchResultsCount()
      return
    }

    if (selectedMake?.models?.length) {
      setModelOptions(
        selectedMake.models.map((model) => ({
          value: model.name,
          label:
            selectedModelOption === model.name
              ? model.name
              : `${model.name} (${model.stock})`,
        })),
      )
    }

    if (selectedModelOption) {
      const selectedModel = selectedMake?.models.find(
        (x) => x.name === selectedModelOption,
      )
      if (selectedModel) {
        setSearchResultsCount(selectedModel.stock)
      }
    } else if (selectedMakeOption) {
      setSearchResultsCount(selectedMake?.stock)
    }
  }, [filteredMakes])

  useEffect(() => {
    //reset filtered makes to all makes
    setFilteredMakes(makes)
  }, [selectedPriceFrequencyOption])

  const onSubmit = () => {
    const urlSegments = [
      `/${vehicleCondition}-cars`,
      ...(selectedMakeOption && selectedMakeOption !== 'any'
        ? [`${dashify(selectedMakeOption)}`]
        : []),
      ...(selectedModelOption && selectedModelOption !== 'any'
        ? [`${dashify(selectedModelOption)}`]
        : []),
    ]

    const urlParams = new URLSearchParams()
    urlParams.append('financeType', 'any')

    const baseUrl = urlSegments.join('/')
    const redirectUrl =
      urlParams.toString().length > 0
        ? `${baseUrl}?${urlParams.toString()}`
        : baseUrl

    window.location.assign(redirectUrl)
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <fieldset className={styles['radioGroup']}>
        {priceFrequencyOptions.map((option) => (
          <div className={styles['radioInputContainer']}>
            <label htmlFor={option.value} className={styles['priceLabel']}>
              <input
                type="radio"
                id={option.value}
                name={option.value}
                checked={option.value === selectedPriceFrequencyOption}
                onChange={() => {
                  setSelectedPriceFrequencyOption(option.value)
                }}
              />
              {option.label}
            </label>
          </div>
        ))}
      </fieldset>
      <Price
        radioChecked={selectedPriceFrequencyOption}
        handleOnChangeTracking={() => console.log('track')}
        maxTotalPrice={selectedMaxTotalPrice}
        setMaxTotalPrice={setSelectedMaxTotalPrice}
        priceBucket={priceBucket}
        monthlyPriceBucket={monthlyPriceBucket}
      />
      <SelectField
        label="Select make"
        labelHidden
        id="make"
        name="make"
        data-testid="make-select"
        options={makeOptions}
        placeholder={'Select make'}
        placeholderValue={'Any'}
        onChange={(event) => {
          const value = event.target.value
          // Only accept values that come from the known options list or the explicit 'any' placeholder
          if (
            value === 'any' ||
            makeOptions.some((option) => option.value === value)
          ) {
            setSelectedMakeOption(value)
          }
        }}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />
      <SelectField
        label="Select model"
        labelHidden
        id="model"
        name="model"
        data-testid="model-select"
        options={modelOptions}
        placeholder={'Select model'}
        placeholderValue={'Any'}
        onChange={(event) => {
          const value = event.target.value
          // Only accept values that come from the known options list or the explicit 'any' placeholder
          if (
            value === 'any' ||
            modelOptions.some((option) => option.value === value)
          ) {
            setSelectedModelOption(value)
          }
        }}
        disabled={
          !selectedMakeOption ||
          selectedMakeOption.toLocaleLowerCase() === 'any'
        }
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />

      <div className={styles['buttonContainer']}>
        <Primary
          type="submit"
          classNames={{ button: styles['button'] }}
          data-testid="searchButton"
          isFullWidth
        >
          {searchResultsCount === 1
            ? 'Search 1 car'
            : `Search ${formatNumber(searchResultsCount)} cars`}
        </Primary>
      </div>
    </form>
  )
}
